<script setup lang="ts">
import { Btn, Dialog } from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import RichHtml from '@/components/RichHtml.vue'
import { useCollection } from '@/composables/useDatastore'

interface PopupRecord {
	id?: string
	_id?: string
	name?: string
	slug?: string
	status?: string
	type?: 'popup' | 'inline'
	startDate?: string
	endDate?: string
	showOnHomepage?: boolean
	appearEvery?: number
	daysToShowAgain?: number
	buttonText?: string
	buttonLink?: string
	popupContent?: string
	updated_at?: string
}

const route = useRoute()
const { items, loading } = useCollection<PopupRecord>('popups', { limit: 100 })
const visiblePopup = ref<PopupRecord | null>(null)
const lastCheckedPath = ref('')

const isHomepage = computed(() => route.path === '/')
const isSuppressedRoute = computed(() =>
	route.path === '/print' || route.path.startsWith('/_blox_preview'),
)

function popupId(popup: PopupRecord) {
	return popup.id || popup._id || popup.slug || popup.name || 'popup'
}

function dateValue(value: string | undefined, endOfDay = false) {
	if (!value) return null
	const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
	const parsed = new Date(dateOnly
		? `${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`
		: value)
	const timestamp = parsed.getTime()
	return Number.isNaN(timestamp) ? null : timestamp
}

function isActive(popup: PopupRecord, now: number) {
	// Migrated records predate the type field and should retain popup behavior.
	if (popup.type === 'inline') return false
	if (popup.status && popup.status !== 'published') return false
	if (!popup.popupContent) return false
	if (isHomepage.value && popup.showOnHomepage !== true) return false

	const startsAt = dateValue(popup.startDate)
	const endsAt = dateValue(popup.endDate, true)
	return (startsAt === null || startsAt <= now) && (endsAt === null || endsAt >= now)
}

function sortNewestFirst(a: PopupRecord, b: PopupRecord) {
	return (dateValue(b.startDate) || dateValue(b.updated_at) || 0)
		- (dateValue(a.startDate) || dateValue(a.updated_at) || 0)
}

function shouldShow(popup: PopupRecord, now: number) {
	if (typeof localStorage === 'undefined') return false

	const id = popupId(popup)
	const lastShown = Number(localStorage.getItem(`site-popup-${id}-last-shown`) || 0)
	const waitDays = Math.max(0, Number(popup.daysToShowAgain) || 0)
	if (lastShown && waitDays > 0 && now - lastShown < waitDays * 86_400_000) return false

	const visitKey = `site-popup-${id}-visits`
	const visits = Number(localStorage.getItem(visitKey) || 0) + 1
	localStorage.setItem(visitKey, String(visits))
	return visits >= Math.max(1, Number(popup.appearEvery) || 1)
}

function checkForPopup() {
	if (isSuppressedRoute.value) {
		visiblePopup.value = null
		return
	}
	if (loading.value || visiblePopup.value || lastCheckedPath.value === route.path) return
	lastCheckedPath.value = route.path

	const now = Date.now()
	const popup = items.value
		.filter(item => isActive(item, now))
		.sort(sortNewestFirst)
		.find(item => shouldShow(item, now))

	if (popup) visiblePopup.value = popup
}

function closePopup() {
	const popup = visiblePopup.value
	if (!popup || typeof localStorage === 'undefined') {
		visiblePopup.value = null
		return
	}

	const id = popupId(popup)
	localStorage.setItem(`site-popup-${id}-last-shown`, String(Date.now()))
	localStorage.setItem(`site-popup-${id}-visits`, '0')
	visiblePopup.value = null
}

watch(
	[loading, () => route.path],
	() => checkForPopup(),
	{ immediate: true },
)
</script>

<template>
	<Dialog
		:open="Boolean(visiblePopup)"
		width="l"
		@update:open="open => { if (!open) closePopup() }"
	>
		<div v-if="visiblePopup" class="popup-text txt-center">
			<RichHtml :html="visiblePopup.popupContent" />
			<Btn
				v-if="visiblePopup.buttonText"
				:href="visiblePopup.buttonLink || undefined"
				color="primary"
				:value="visiblePopup.buttonText"
				class="mt-1"
				@click="closePopup"
			/>
		</div>
	</Dialog>
</template>
